import { useEffect, useState, useMemo } from "react";
import * as spine from "@esotericsoftware/spine-webgl";
const { Physics } = spine;
import { useConfigStore } from "../store/configStore";
import { MultiAtlasAttachmentLoader } from "../lib/multiAtlasLoader";
import { cn } from "@/lib/utils";

// 全局缓存
const thumbnailCache = new Map<string, string>();

// 全局共享的渲染器缓存 (spineDataId -> Renderer)
const rendererCache = new Map<
  string,
  {
    canvas: HTMLCanvasElement;
    spineCanvas: spine.SpineCanvas | null;
    skeleton: spine.Skeleton | null;
    isInitialized: boolean;
    initPromise: Promise<any> | null;
  }
>();

// 渲染队列
type RenderTask = {
  skinName: string;
  size: number;
  spineData: any; // 任务需要携带 spineData
  resolve: (dataUrl: string) => void;
  reject: (error: Error) => void;
};
let renderQueue: RenderTask[] = [];
let isProcessing = false;

interface SkinThumbnailProps {
  skinName: string;
  size?: number;
  className?: string;
  spineData?: any; // 可选传入 spineData
}

export function SkinThumbnail({
  skinName,
  size = 64,
  className,
  spineData: propSpineData,
}: SkinThumbnailProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const { spineData: storeSpineData } = useConfigStore();

  // 优先使用传入的 spineData，否则使用 store 的
  const targetSpineData = propSpineData || storeSpineData;

  // spineData 唯一标识
  const spineDataId = useMemo(() => {
    if (!targetSpineData?.json?.skeleton) return null;
    // 使用更可靠的 ID，比如 json 对象的引用或者计算 hash
    // 这里简单使用 json 字符串长度 + skinName 数量作为简单指纹，或者假设传入的对象引用稳定
    // 如果是关联模版，通常对象引用是稳定的
    return `${targetSpineData.json.skeleton.hash || ""}-${targetSpineData.json.skeleton.spine || ""}-${Object.keys(targetSpineData.json.skins || {}).length}`;
  }, [targetSpineData]);

  const cacheKey = useMemo(() => {
    if (!spineDataId) return null;
    return `${spineDataId}-${skinName}-${size}`;
  }, [spineDataId, skinName, size]);

  useEffect(() => {
    if (!targetSpineData || !skinName || !spineDataId) return;

    // 检查缓存
    if (cacheKey && thumbnailCache.has(cacheKey)) {
      setImageUrl(thumbnailCache.get(cacheKey)!);
      return;
    }

    // 加入渲染队列
    const task = new Promise<string>((resolve, reject) => {
      renderQueue.push({
        skinName,
        size,
        spineData: targetSpineData,
        resolve,
        reject,
      });
    });

    task
      .then((dataUrl) => {
        if (cacheKey) thumbnailCache.set(cacheKey, dataUrl);
        setImageUrl(dataUrl);
        setError(false);
      })
      .catch((err) => {
        console.error(`[SkinThumbnail] Failed: ${skinName}`, err);
        setError(true);
      });

    // 开始处理队列
    processQueue();

    return () => {
      // 从队列中移除未处理的任务
      renderQueue = renderQueue.filter(
        (t) => !(t.skinName === skinName && t.spineData === targetSpineData)
      );
    };
  }, [targetSpineData, skinName, spineDataId, cacheKey, size]);

  if (error) {
    return (
      <div
        className={cn(
          "bg-muted rounded flex items-center justify-center text-muted-foreground text-xs",
          className
        )}
        style={{ width: size, height: size }}
      >
        ?
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div
        className={cn("bg-muted rounded animate-pulse", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <img
      src={imageUrl}
      alt={skinName}
      className={cn("rounded object-contain", className)}
      style={{ width: size, height: size }}
    />
  );
}

// 获取或初始化渲染器
function getRenderer(spineData: any, spineDataId: string): Promise<any> {
  let renderer = rendererCache.get(spineDataId);

  if (!renderer) {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    canvas.style.display = "none";
    // 调试模式下可以添加到 body
    // document.body.appendChild(canvas);
    
    renderer = {
      canvas,
      spineCanvas: null,
      skeleton: null,
      isInitialized: false,
      initPromise: null,
    };
    rendererCache.set(spineDataId, renderer);
  }

  if (renderer.isInitialized && renderer.skeleton) {
    return Promise.resolve(renderer);
  }

  if (renderer.initPromise) {
    return renderer.initPromise.then(() => renderer);
  }

  renderer.initPromise = new Promise((resolve, reject) => {
    const currentRenderer = renderer!;

    const app = {
      loadAssets(spineCanvas: spine.SpineCanvas) {
        // ... (同上，使用 spineData)
      },
      initialize(spineCanvas: spine.SpineCanvas) {
        try {
          const assetManager = spineCanvas.assetManager;

          // 手动加载资源到 assetManager
          // 加载骨骼数据（JSON 或 binary）
          if (spineData.skelBytes) {
            const skelBytes = spineData.skelBytes as Uint8Array;
            let binary = "";
            for (let i = 0; i < skelBytes.byteLength; i++) {
              binary += String.fromCharCode(skelBytes[i]);
            }
            const base64 = btoa(binary);
            assetManager.setRawDataURI(
              "skeleton.skel",
              "data:application/octet-stream;base64," + base64,
            );
            assetManager.loadBinary("skeleton.skel");
          } else {
            assetManager.setRawDataURI(
              "skeleton.json",
              "data:application/json;base64," +
                btoa(unescape(encodeURIComponent(JSON.stringify(spineData.json))))
            );
            assetManager.loadJson("skeleton.json");
          }

          // 设置所有 PNG 数据
          if (spineData.pngDataUrls) {
            for (const [fileName, dataUrl] of Object.entries(
              spineData.pngDataUrls
            )) {
              assetManager.setRawDataURI(fileName, dataUrl as string);
            }
          }

          // 加载所有 Atlas 文件
          const atlasTexts = spineData.atlasTexts as Record<string, string>;
          const atlasFileNames = Object.keys(atlasTexts);
          for (const [atlasFileName, atlasText] of Object.entries(atlasTexts)) {
            assetManager.setRawDataURI(
              atlasFileName,
              "data:text/plain;base64," +
                btoa(unescape(encodeURIComponent(atlasText)))
            );
            assetManager.loadTextureAtlas(atlasFileName);
          }

// 等待资源加载完成
          // spine-webgl AssetManager 没有直接的 wait 方法，通常依赖 loadTextureAtlas 的回调或轮询 isLoadingComplete
          // 这里我们简单轮询一下，确保所有 setRawDataURI 的资源被内部处理
          const waitForAssets = async () => {
             while (!assetManager.isLoadingComplete()) {
                await new Promise(r => requestAnimationFrame(r));
             }
          };
          
          waitForAssets().then(() => {
              try {
                  // ... Loader creation logic ...
                  let atlasLoader: spine.AttachmentLoader;
                  if (atlasFileNames.length === 1) {
                    const atlas = assetManager.require(atlasFileNames[0]) as spine.TextureAtlas;
                    atlasLoader = new spine.AtlasAttachmentLoader(atlas);
                  } else {
                    const atlases = atlasFileNames.map((fileName) =>
                      assetManager.require(fileName)
                    ) as spine.TextureAtlas[];
                    atlasLoader = new MultiAtlasAttachmentLoader(atlases);
                  }

                  let skeletonData: spine.SkeletonData;
                  if (spineData.skelBytes) {
                    const skeletonBinary = new spine.SkeletonBinary(atlasLoader);
                    // 从 assetManager require 二进制数据
                    skeletonData = skeletonBinary.readSkeletonData(
                      assetManager.require("skeleton.skel")
                    );
                  } else {
                    const skeletonJson = new spine.SkeletonJson(atlasLoader);
                    skeletonData = skeletonJson.readSkeletonData(
                      assetManager.require("skeleton.json")
                    );
                  }

                  currentRenderer.skeleton = new spine.Skeleton(skeletonData);
                  currentRenderer.spineCanvas = spineCanvas;
                  currentRenderer.isInitialized = true;
                  resolve(currentRenderer);
              } catch (e) {
                  reject(e);
              }
          });
        } catch (err) {
          reject(err);
        }
      },
      update() {},
      render() {},
      error(_: spine.SpineCanvas, errors: spine.StringMap<string>) {
        reject(new Error(Object.values(errors).join(", ")));
      }
    };

    new spine.SpineCanvas(currentRenderer.canvas, {
      app,
      webglConfig: {
        alpha: true,
        preserveDrawingBuffer: true,
        premultipliedAlpha: false,
        antialias: false,
      },
    });
  });

  return renderer.initPromise.then(() => renderer);
}

// 处理渲染队列
async function processQueue() {
  if (isProcessing || renderQueue.length === 0) return;
  isProcessing = true;

  try {
    while (renderQueue.length > 0) {
      const task = renderQueue.shift()!;
      try {
        const spineDataId = `${task.spineData.json.skeleton.hash || ""}-${task.spineData.json.skeleton.spine || ""}-${Object.keys(task.spineData.json.skins || {}).length}`;
        const renderer = await getRenderer(task.spineData, spineDataId);
        const dataUrl = await renderSkin(renderer, task.skinName, task.size);
        task.resolve(dataUrl);
      } catch (err) {
        task.reject(err instanceof Error ? err : new Error(String(err)));
      }
      await new Promise((r) => setTimeout(r, 10));
    }
  } finally {
    isProcessing = false;
    // 如果有新任务加入，继续处理
    if (renderQueue.length > 0) {
      processQueue();
    }
  }
}

// 渲染单个 skin
function renderSkin(renderer: any, skinName: string, size: number): Promise<string> {
  return new Promise((resolve, reject) => {
    if (
      !renderer?.isInitialized ||
      !renderer.skeleton ||
      !renderer.spineCanvas
    ) {
      reject(new Error("Renderer not initialized"));
      return;
    }

    const { skeleton, spineCanvas, canvas } = renderer;
    const skeletonData = skeleton.data;

    // ... (渲染逻辑同上)
    // 调整 canvas 大小
    const renderSize = size * 2;
    if (canvas.width !== renderSize) {
        canvas.width = renderSize;
        canvas.height = renderSize;
    }

    // 找到目标 skin
    const targetSkin = skeletonData.skins.find((s: any) => s.name === skinName);
    if (!targetSkin) {
      reject(new Error(`Skin not found: ${skinName}`));
      return;
    }

    // 设置 skin
    skeleton.setSkin(targetSkin);
    skeleton.setToSetupPose();
    skeleton.updateWorldTransform(Physics.update);

    // 计算边界
    const offset = new spine.Vector2();
    const boundsSize = new spine.Vector2();
    skeleton.getBounds(offset, boundsSize);

    // 检查边界是否有效
    if (boundsSize.x <= 0 || boundsSize.y <= 0) {
      reject(new Error(`Invalid bounds for skin: ${skinName}`));
      return;
    }

    // 更新 viewport
    const gl = spineCanvas.gl;
    gl.viewport(0, 0, renderSize, renderSize);

    // 设置相机
    const camera = spineCanvas.renderer.camera;
    camera.position.x = offset.x + boundsSize.x / 2;
    camera.position.y = offset.y + boundsSize.y / 2;
    camera.viewportWidth = renderSize;
    camera.viewportHeight = renderSize;

    // 计算 zoom
    const padding = 1.15;
    const zoomX = (boundsSize.x * padding) / renderSize;
    const zoomY = (boundsSize.y * padding) / renderSize;
    camera.zoom = Math.max(zoomX, zoomY);
    camera.update();

    // 清空并渲染
    spineCanvas.clear(0, 0, 0, 0);
    spineCanvas.renderer.begin();
    spineCanvas.renderer.drawSkeleton(skeleton, true);
    spineCanvas.renderer.end();

    // 导出
    try {
      const dataUrl = canvas.toDataURL("image/png");
      resolve(dataUrl);
    } catch (err) {
      reject(err);
    }
  });
}

// 清除缓存
export function clearSkinThumbnailCache() {
  thumbnailCache.clear();
  // 清理并销毁所有 renderer
  for (const renderer of rendererCache.values()) {
    if (renderer.spineCanvas) {
      // renderer.spineCanvas.dispose(); // 如果有 dispose 方法
    }
    // 移除 canvas 元素
    if (renderer.canvas && renderer.canvas.parentNode) {
      renderer.canvas.parentNode.removeChild(renderer.canvas);
    }
  }
  rendererCache.clear();
  renderQueue = [];
}
